import { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Shield, Clock, TrendingUp, FileCheck, Lock, BarChart3, HeadphonesIcon, ArrowDown } from "lucide-react";

const SellerRegistration = () => {
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const d = (e.data || {}) as { source?: string; type?: string; height?: number };
      if (d.source === "onboarding-widget" && d.type === "truevalue:resize") {
        const f = document.getElementById("truevalue-onboarding") as HTMLIFrameElement | null;
        if (f && d.height) f.style.height = d.height + "px";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const benefits = [
    {
      icon: BarChart3,
      title: "Free Business Valuation",
      description: "Confidential valuation delivered within 48 hours, no obligation.",
    },
    {
      icon: Lock,
      title: "100% Confidential",
      description: "Your sale is handled discreetly — staff, clients, and competitors never know.",
    },
    {
      icon: FileCheck,
      title: "Pre-Qualified Buyers",
      description: "We only introduce serious, vetted acquirers who are the right fit.",
    },
    {
      icon: HeadphonesIcon,
      title: "End-to-End Support",
      description: "From valuation through to completion, we manage the entire process.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Shareholder Registration | VAMOS M&A Advisory"
        description="Start your confidential business sale with VAMOS. Sign our NDA and complete the onboarding form to begin a confidential initial valuation."
        canonical="/seller-registration"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-background via-background to-secondary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block px-6 py-3 rounded-full bg-accent/10 border border-accent/30 mb-8">
            <span className="text-sm font-bold text-accent uppercase tracking-wider">Seller Onboarding</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Start Your <span className="text-accent">Confidential Sale</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Complete the two steps below to begin the process. We'll deliver your free business valuation within 48 hours.
          </p>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-8 border-b border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Shield className="w-6 h-6 text-accent" />
              <span className="text-xs md:text-sm text-muted-foreground font-medium">100% Confidential</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="w-6 h-6 text-accent" />
              <span className="text-xs md:text-sm text-muted-foreground font-medium">48-Hour Turnaround</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TrendingUp className="w-6 h-6 text-accent" />
              <span className="text-xs md:text-sm text-muted-foreground font-medium">£1M–£50M Range</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits row */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">What You Get</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="bg-card rounded-xl border border-border p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mx-auto mb-3">
                  <benefit.icon className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-semibold text-foreground text-sm mb-1">{benefit.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two-step forms */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 space-y-0">
          {/* Step 1: NDA */}
          <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
            <div className="p-6 border-b border-border flex items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                1
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-lg">Sign the Non-Disclosure Agreement</h3>
                <p className="text-sm text-muted-foreground">Please review and sign our NDA to ensure full confidentiality throughout the process.</p>
              </div>
            </div>
            <iframe
              src="https://form.pandadoc.com/form/ygePi44Ra2UzFmNLFTvkv9"
              width="100%"
              height="700"
              style={{ border: "none" }}
              title="Seller NDA Form"
            />
          </div>

          {/* Connector */}
          <div className="flex flex-col items-center py-6">
            <div className="w-px h-8 bg-border" />
            <div className="px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-sm font-medium text-accent">
              Then
            </div>
            <ArrowDown className="w-5 h-5 text-accent mt-2" />
          </div>

          {/* Step 2: Onboarding / Valuation Form */}
          <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
            <div className="p-6 border-b border-border flex items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                2
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-lg">Complete Seller Onboarding</h3>
                <p className="text-sm text-muted-foreground">Tell us about your business so we can prepare an initial valuation and exit assessment.</p>
              </div>
            </div>
            <div className="flex justify-center p-4">
              <iframe
                id="truevalue-onboarding"
                src="https://www.truevalue.business/public/onboarding/06657373-ee7a-491c-9156-8f70f311a53e?embed=true"
                style={{ width: "100%", border: 0, minHeight: "900px", borderRadius: "12px", background: "transparent", maxWidth: "800px" }}
                scrolling="no"
                allow="clipboard-write"
                title="Seller Onboarding Form"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SellerRegistration;
